class CookieConsentManager {
  cookieName = 'lh_cookie_consent'
  expirationDays = 180
  domain = '.lifehikes.com'
  privacyPolicyUrl = 'https://lifehikes.com/privacy-policy/' // Default fallback
  currentConsent = null
  callbacks = []
  initialized = false
  bannerEl = null
  modalEl = null
  styleEl = null

  init(options) {
    if (this.initialized) return
    if (options) {
      if (options.cookieName) this.cookieName = options.cookieName
      if (options.expirationDays) this.expirationDays = options.expirationDays
      if (options.domain) this.domain = options.domain
      if (options.privacyPolicyUrl) this.privacyPolicyUrl = options.privacyPolicyUrl
    }
    this.currentConsent = this.readConsentCookie()
    if (!this.currentConsent) {
      if (document.body) {
        this.showBannerAndModal()
      } else {
        document.addEventListener('DOMContentLoaded', () => this.showBannerAndModal())
      }
    }
    this.initialized = true
  }

  getConsentState() {
    return this.currentConsent
  }

  hasConsent(category) {
    if (category === 'essential') return true
    return this.currentConsent ? !!this.currentConsent[category] : false
  }

  onConsentChange(callback) {
    this.callbacks.push(callback)
    if (this.currentConsent) {
      try {
        callback(this.currentConsent)
      } catch (err) {
        console.error('Error in cookie consent callback:', err)
      }
    }
    return () => {
      this.callbacks = this.callbacks.filter((cb) => cb !== callback)
    }
  }

  showPreferences() {
    if (!this.bannerEl || !this.modalEl) {
      this.showBannerAndModal()
    }
    const consent = this.currentConsent || {
      essential: true,
      analytics: false,
      timestamp: ''
    }
    const analyticsCheckbox = document.getElementById('lh-cc-analytics')
    if (analyticsCheckbox) analyticsCheckbox.checked = consent.analytics
    if (this.modalEl) {
      this.modalEl.classList.add('lh-cc-active')
      const card = this.modalEl.querySelector('.lh-cc-modal-card')
      if (card) card.classList.add('lh-cc-active')
      const closeBtn = document.getElementById('lh-cc-modal-close')
      if (closeBtn) closeBtn.focus()
    }
  }

  showBannerAndModal() {
    this.injectStyles()
    this.createModal()
    if (!this.currentConsent) {
      this.createBanner()
    }
  }

  readConsentCookie() {
    const nameEQ = this.cookieName + '='
    const decodedCookie = decodeURIComponent(document.cookie)
    const ca = decodedCookie.split(';')
    for (let i = 0; i < ca.length; i++) {
      let c = ca[i].trim()
      if (c.indexOf(nameEQ) === 0) {
        try {
          const jsonStr = c.substring(nameEQ.length)
          const parsed = JSON.parse(jsonStr)
          if (parsed && typeof parsed.essential === 'boolean') {
            return parsed
          }
        } catch (e) {
          console.warn('Failed to parse cookie consent JSON:', e)
        }
      }
    }
    return null
  }

  writeConsentCookie(consent) {
    const value = JSON.stringify(consent)
    const maxAge = this.expirationDays * 24 * 60 * 60
    const hostname = window.location.hostname
    const isDomainLifehikes = hostname.endsWith('lifehikes.com')
    const domainStr = isDomainLifehikes ? `; Domain=${this.domain}` : ''
    const isSecure = window.location.protocol === 'https:' || hostname === 'localhost' || hostname === '127.0.0.1'
    const secureStr = isSecure ? '; Secure' : ''
    
    document.cookie = `${this.cookieName}=${encodeURIComponent(value)}${domainStr}; Path=/; SameSite=Lax; Max-Age=${maxAge}${secureStr}`
    this.currentConsent = consent
    this.triggerCallbacks(consent)
  }

  triggerCallbacks(consent) {
    this.callbacks.forEach((callback) => {
      try {
        callback(consent)
      } catch (err) {
        console.error('Error in cookie consent callback:', err)
      }
    })
  }

  injectStyles() {
    if (this.styleEl) return
    const style = document.createElement('style')
    style.id = 'lh-cookie-consent-styles'
    style.innerHTML = `
      .lh-cc-container {
        font-family: 'Source Sans Pro', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
        box-sizing: border-box;
      }
      .lh-cc-container * {
        box-sizing: border-box;
        margin: 0;
        padding: 0;
      }
      .lh-cc-banner {
        position: fixed;
        bottom: 24px;
        right: 24px;
        max-width: 440px;
        width: calc(100% - 48px);
        background: rgba(255, 255, 255, 0.95);
        backdrop-filter: blur(16px) saturate(180%);
        -webkit-backdrop-filter: blur(16px) saturate(180%);
        border: 1px solid rgba(226, 232, 240, 0.8);
        border-radius: 16px;
        box-shadow: 0 12px 36px rgba(0, 0, 0, 0.12);
        padding: 24px;
        z-index: 99999;
        display: flex;
        flex-direction: column;
        gap: 16px;
        opacity: 0;
        transform: translateY(40px);
        transition: opacity 0.5s cubic-bezier(0.16, 1, 0.3, 1), transform 0.5s cubic-bezier(0.16, 1, 0.3, 1);
      }
      .lh-cc-banner.lh-cc-show {
        opacity: 1;
        transform: translateY(0);
      }
      .lh-cc-title-row {
        display: flex;
        align-items: center;
        gap: 10px;
      }
      .lh-cc-icon {
        width: 24px;
        height: 24px;
        fill: #0056FF;
      }
      .lh-cc-title {
        font-size: 18px;
        font-weight: 700;
        color: #282B2E;
      }
      .lh-cc-description {
        font-size: 14px;
        line-height: 1.5;
        color: #5A626A;
      }
      .lh-cc-policy-link {
        color: #0056FF;
        text-decoration: none;
        font-weight: 600;
      }
      .lh-cc-policy-link:hover {
        text-decoration: underline;
      }
      .lh-cc-actions {
        display: flex;
        flex-direction: column;
        gap: 10px;
        width: 100%;
      }
      .lh-cc-button-group {
        display: flex;
        gap: 12px;
        width: 100%;
      }
      .lh-cc-btn {
        flex: 1;
        padding: 11px 16px;
        font-size: 14px;
        font-weight: 600;
        border-radius: 8px;
        border: none;
        cursor: pointer;
        transition: background-color 0.2s, color 0.2s, transform 0.1s, border-color 0.2s;
        text-align: center;
        white-space: nowrap;
      }
      .lh-cc-btn-primary {
        background-color: #0056FF;
        color: #ffffff;
      }
      .lh-cc-btn-primary:hover {
        background-color: #0040bd;
      }
      .lh-cc-btn-primary:active {
        transform: scale(0.98);
      }
      .lh-cc-btn-secondary {
        background-color: transparent;
        color: #282B2E;
        border: 1px solid #CED4D9;
      }
      .lh-cc-btn-secondary:hover {
        background-color: rgba(40, 43, 46, 0.05);
        border-color: #282B2E;
      }
      .lh-cc-btn-secondary:active {
        transform: scale(0.98);
      }
      .lh-cc-manage-link {
        font-size: 14px;
        font-weight: 600;
        color: #0056FF;
        background: none;
        border: none;
        cursor: pointer;
        text-align: center;
        text-decoration: underline;
        padding: 4px 8px;
        align-self: center;
      }
      .lh-cc-manage-link:hover {
        color: #0040bd;
      }
      .lh-cc-modal-overlay {
        position: fixed;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        background-color: rgba(26, 32, 44, 0.5);
        backdrop-filter: blur(8px);
        -webkit-backdrop-filter: blur(8px);
        display: flex;
        align-items: center;
        justify-content: center;
        z-index: 100000;
        opacity: 0;
        pointer-events: none;
        transition: opacity 0.3s cubic-bezier(0.16, 1, 0.3, 1);
      }
      .lh-cc-modal-overlay.lh-cc-active {
        opacity: 1;
        pointer-events: auto;
      }
      .lh-cc-modal-card {
        background-color: #ffffff;
        border-radius: 20px;
        width: 100%;
        max-width: 520px;
        box-shadow: 0 24px 60px rgba(0, 0, 0, 0.15);
        padding: 32px;
        margin: 20px;
        transform: scale(0.94);
        transition: transform 0.3s cubic-bezier(0.16, 1, 0.3, 1);
        display: flex;
        flex-direction: column;
        gap: 24px;
        max-height: calc(100vh - 40px);
        overflow-y: auto;
      }
      .lh-cc-modal-card.lh-cc-active {
        transform: scale(1);
      }
      .lh-cc-modal-header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        border-bottom: 1px solid #F0F6F8;
        padding-bottom: 16px;
      }
      .lh-cc-modal-title {
        font-size: 20px;
        font-weight: 700;
        color: #282B2E;
      }
      .lh-cc-modal-close-btn {
        background: none;
        border: none;
        cursor: pointer;
        font-size: 24px;
        color: #CED4D9;
        display: flex;
        align-items: center;
        justify-content: center;
        width: 32px;
        height: 32px;
        border-radius: 50%;
        transition: background-color 0.2s, color 0.2s;
      }
      .lh-cc-modal-close-btn:hover {
        background-color: #F0F6F8;
        color: #282B2E;
      }
      .lh-cc-modal-body {
        display: flex;
        flex-direction: column;
        gap: 20px;
      }
      .lh-cc-category-card {
        display: flex;
        justify-content: space-between;
        align-items: flex-start;
        gap: 16px;
        padding: 16px;
        background-color: #F0F6F8;
        border-radius: 12px;
        transition: background-color 0.2s;
      }
      .lh-cc-category-card:hover {
        background-color: #E2EBF0;
      }
      .lh-cc-category-details {
        display: flex;
        flex-direction: column;
        gap: 4px;
        flex: 1;
      }
      .lh-cc-category-header {
        display: flex;
        align-items: center;
        gap: 8px;
      }
      .lh-cc-category-name {
        font-size: 15px;
        font-weight: 600;
        color: #282B2E;
      }
      .lh-cc-badge-required {
        font-size: 11px;
        font-weight: 700;
        color: #0056FF;
        background-color: #e0ebff;
        padding: 2px 8px;
        border-radius: 12px;
        text-transform: uppercase;
      }
      .lh-cc-category-desc {
        font-size: 13px;
        line-height: 1.5;
        color: #5A626A;
      }
      .lh-cc-switch {
        position: relative;
        display: inline-block;
        width: 46px;
        height: 26px;
        flex-shrink: 0;
      }
      .lh-cc-switch input {
        opacity: 0;
        width: 0;
        height: 0;
      }
      .lh-cc-slider {
        position: absolute;
        cursor: pointer;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        background-color: #CED4D9;
        transition: .3s;
        border-radius: 34px;
      }
      .lh-cc-slider:before {
        position: absolute;
        content: "";
        height: 20px;
        width: 20px;
        left: 3px;
        bottom: 3px;
        background-color: #ffffff;
        transition: .3s;
        border-radius: 50%;
        box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
      }
      .lh-cc-switch input:checked + .lh-cc-slider {
        background-color: #0056FF;
      }
      .lh-cc-switch input:checked + .lh-cc-slider:before {
        transform: translateX(20px);
      }
      .lh-cc-switch input:disabled + .lh-cc-slider {
        background-color: #D8DEE2;
        cursor: not-allowed;
      }
      .lh-cc-switch input:disabled + .lh-cc-slider:before {
        background-color: #f5f5f5;
      }
      .lh-cc-btn:focus-visible,
      .lh-cc-manage-link:focus-visible,
      .lh-cc-modal-close-btn:focus-visible,
      .lh-cc-policy-link:focus-visible {
        outline: 3px solid #0056FF;
        outline-offset: 2px;
      }
      .lh-cc-switch input:focus-visible + .lh-cc-slider {
        outline: 3px solid #0056FF;
        outline-offset: 3px;
      }
      .lh-cc-modal-footer {
        display: flex;
        justify-content: flex-end;
        gap: 12px;
        border-top: 1px solid #F0F6F8;
        padding-top: 16px;
      }
      .lh-cc-btn-save {
        background-color: #0056FF;
        color: #ffffff;
        flex: none;
        width: 150px;
      }
      .lh-cc-btn-save:hover {
        background-color: #0040bd;
      }
      @media (max-width: 480px) {
        .lh-cc-banner {
          bottom: 0;
          right: 0;
          width: 100%;
          max-width: 100%;
          border-radius: 16px 16px 0 0;
          border-left: none;
          border-right: none;
          border-bottom: none;
          padding: 20px;
        }
        .lh-cc-button-group {
          flex-direction: column;
        }
        .lh-cc-modal-card {
          margin: 10px;
          padding: 20px;
          gap: 18px;
        }
        .lh-cc-category-card {
          padding: 12px;
        }
      }
    `
    this.styleEl = style
    document.head.appendChild(style)
  }

  createBanner() {
    if (this.bannerEl) return
    const container = document.createElement('div')
    container.className = 'lh-cc-container'
    const banner = document.createElement('div')
    banner.className = 'lh-cc-banner'
    banner.setAttribute('role', 'status')
    banner.setAttribute('aria-live', 'polite')
    banner.innerHTML = `
      <div class="lh-cc-title-row">
        <svg class="lh-cc-icon" viewBox="0 0 24 24">
          <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1.5 16.5c-.83 0-1.5-.67-1.5-1.5s.67-1.5 1.5-1.5 1.5.67 1.5 1.5-.67 1.5-1.5 1.5zm3-5c-.83 0-1.5-.67-1.5-1.5s.67-1.5 1.5-1.5 1.5.67 1.5 1.5-.67 1.5-1.5 1.5zm-9 0c-.83 0-1.5-.67-1.5-1.5s.67-1.5 1.5-1.5 1.5.67 1.5 1.5-.67 1.5-1.5 1.5zm2-5c-.83 0-1.5-.67-1.5-1.5S9.17 5 10 5s1.5.67 1.5 1.5S10.83 7 10 7zm5 0c-.83 0-1.5-.67-1.5-1.5S14.17 5 15 5s1.5.67 1.5 1.5S15.83 7 15 7z"/>
        </svg>
        <span class="lh-cc-title">We Value Your Privacy</span>
      </div>
      <p class="lh-cc-description">
        We use cookies to secure user sessions and analyze web traffic. Learn more in our 
        <a class="lh-cc-policy-link" href="${this.privacyPolicyUrl}" target="_blank" rel="noopener">Privacy Policy</a>.
      </p>
      <div class="lh-cc-actions">
        <div class="lh-cc-button-group">
          <button class="lh-cc-btn lh-cc-btn-primary" id="lh-cc-accept-all">Accept All</button>
          <button class="lh-cc-btn lh-cc-btn-secondary" id="lh-cc-reject-all">Reject Non-Essential</button>
        </div>
        <button class="lh-cc-manage-link" id="lh-cc-manage-settings">Cookie Settings</button>
      </div>
    `
    container.appendChild(banner)
    document.body.appendChild(container)
    this.bannerEl = banner
    setTimeout(() => {
      if (banner) banner.classList.add('lh-cc-show')
    }, 100)
    
    document.getElementById('lh-cc-accept-all')?.addEventListener('click', () => this.handleAcceptAll())
    document.getElementById('lh-cc-reject-all')?.addEventListener('click', () => this.handleRejectNonEssential())
    document.getElementById('lh-cc-manage-settings')?.addEventListener('click', () => this.showPreferences())
  }

  createModal() {
    if (this.modalEl) return
    const overlay = document.createElement('div')
    overlay.className = 'lh-cc-modal-overlay lh-cc-container'
    overlay.id = 'lh-cc-modal-overlay'
    overlay.setAttribute('role', 'dialog')
    overlay.setAttribute('aria-modal', 'true')
    overlay.setAttribute('aria-labelledby', 'lh-cc-modal-title')
    overlay.innerHTML = `
      <div class="lh-cc-modal-card">
        <div class="lh-cc-modal-header">
          <h2 class="lh-cc-modal-title" id="lh-cc-modal-title">Cookie Settings</h2>
          <button class="lh-cc-modal-close-btn" id="lh-cc-modal-close" aria-label="Close settings">&times;</button>
        </div>
        <div class="lh-cc-modal-body">
          <div class="lh-cc-category-card">
            <div class="lh-cc-category-details">
              <div class="lh-cc-category-header">
                <span class="lh-cc-category-name">Strictly Necessary</span>
                <span class="lh-cc-badge-required">Always Active</span>
              </div>
              <p class="lh-cc-category-desc">
                Required for core website security, authenticating users, and managing SSO login sessions. They cannot be disabled.
              </p>
            </div>
            <label class="lh-cc-switch">
              <input type="checkbox" checked disabled aria-label="Strictly Necessary cookies toggle">
              <span class="lh-cc-slider"></span>
            </label>
          </div>
          <div class="lh-cc-category-card">
            <div class="lh-cc-category-details">
              <div class="lh-cc-category-header">
                <span class="lh-cc-category-name">Analytics Cookies</span>
              </div>
              <p class="lh-cc-category-desc">
                Allows us to track page views and interactions to measure and improve our application performance and user experience.
              </p>
            </div>
            <label class="lh-cc-switch">
              <input type="checkbox" id="lh-cc-analytics" aria-label="Analytics cookies toggle">
              <span class="lh-cc-slider"></span>
            </label>
          </div>
        </div>
        <div class="lh-cc-modal-footer">
          <button class="lh-cc-btn lh-cc-btn-secondary" id="lh-cc-modal-cancel">Cancel</button>
          <button class="lh-cc-btn lh-cc-btn-primary lh-cc-btn-save" id="lh-cc-save-settings">Save Preferences</button>
        </div>
      </div>
    `
    document.body.appendChild(overlay)
    this.modalEl = overlay
    
    document.getElementById('lh-cc-modal-close')?.addEventListener('click', () => this.hideModal())
    document.getElementById('lh-cc-modal-cancel')?.addEventListener('click', () => this.hideModal())
    document.getElementById('lh-cc-save-settings')?.addEventListener('click', () => this.handleSavePreferences())
    overlay.addEventListener('click', (e) => {
      if (e.target === overlay) this.hideModal()
    })
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && overlay.classList.contains('lh-cc-active')) this.hideModal()
    })
  }

  hideModal() {
    if (this.modalEl) {
      this.modalEl.classList.remove('lh-cc-active')
      const card = this.modalEl.querySelector('.lh-cc-modal-card')
      if (card) card.classList.remove('lh-cc-active')
    }
  }

  dismissBanner() {
    if (this.bannerEl) {
      this.bannerEl.classList.remove('lh-cc-show')
      const banner = this.bannerEl
      setTimeout(() => {
        banner.parentElement?.remove()
      }, 500)
      this.bannerEl = null
    }
  }

  handleAcceptAll() {
    const consent = {
      essential: true,
      analytics: true,
      timestamp: new Date().toISOString()
    }
    this.writeConsentCookie(consent)
    this.dismissBanner()
    this.hideModal()
  }

  handleRejectNonEssential() {
    const consent = {
      essential: true,
      analytics: false,
      timestamp: new Date().toISOString()
    }
    this.writeConsentCookie(consent)
    this.dismissBanner()
    this.hideModal()
  }

  handleSavePreferences() {
    const analyticsCheckbox = document.getElementById('lh-cc-analytics')
    const consent = {
      essential: true,
      analytics: analyticsCheckbox ? analyticsCheckbox.checked : false,
      timestamp: new Date().toISOString()
    }
    this.writeConsentCookie(consent)
    this.dismissBanner()
    this.hideModal()
  }
}

export const cookieConsentManager = new CookieConsentManager()
export default cookieConsentManager
